import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Save, Trash2, CalendarClock, Activity } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";

const inp = "h-10 w-full px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm placeholder-slate-500 outline-none focus:border-[#E11D2A]";
const fmt = (d) => (d ? new Date(d).toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const KeySlot = ({ slot, title, hint, info, onDone }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await endpoints.sbAdminSaveKey(slot, v.trim()); setV(""); toast.success(`${title} saved securely`); onDone(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail) || "Could not save the key"); }
    setBusy(false);
  };
  const clear = async () => {
    if (!window.confirm(`Remove the ${title.toLowerCase()}?`)) return;
    try { await endpoints.sbAdminClearKey(slot); toast.success("Key removed"); onDone(); } catch { toast.error("Could not remove the key"); }
  };
  return (
    <div className="rounded-xl bg-[#0A0C10]/60 border border-[#273042] p-4" data-testid={`sb-key-${slot}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-white text-sm font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#E11D2A]" />{title}</p>
        <span data-testid={`sb-key-${slot}-status`} className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${info?.set ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
          {info?.set ? `Saved · ${fmt(info.updatedAt)}` : "Not set"}</span>
      </div>
      <p className="text-xs text-slate-400 mb-3">{hint}</p>
      <div className="flex flex-wrap sm:flex-nowrap gap-2">
        <input type="password" autoComplete="new-password" spellCheck={false} value={v} onChange={(e) => setV(e.target.value)}
          placeholder={info?.set ? "Enter a new value to replace the saved key" : "Paste the ScoreBat API token"} className={inp} data-testid={`sb-key-${slot}-input`} />
        <button onClick={save} disabled={busy || v.trim().length < 20} data-testid={`sb-key-${slot}-save`}
          className="h-10 px-4 shrink-0 rounded-lg bg-[#E11D2A] disabled:opacity-40 text-white text-sm font-semibold inline-flex items-center gap-1.5"><Save className="w-4 h-4" />Save</button>
        {info?.set && <button onClick={clear} data-testid={`sb-key-${slot}-clear`} className="h-10 w-10 shrink-0 rounded-lg border border-[#273042] text-slate-400 hover:text-white inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-[#0A0C10]/60 border border-[#273042] px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-white text-sm font-semibold mt-0.5" data-testid={testid}>{value}</p>
  </div>
);

export function ScoreBatKeyPanel() {
  const [s, setS] = useState(null);
  const load = () => endpoints.sbAdminStatus().then((r) => setS(r.data)).catch(() => toast.error("Could not load ScoreBat settings"));
  useEffect(() => { load(); }, []);
  return (
    <div className="rounded-xl bg-[#121620] border border-[#273042] p-5 mb-6" data-testid="sb-key-panel">
      <h2 className="text-white font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#E11D2A]" /> ScoreBat highlights · API key</h2>
      <p className="text-xs text-slate-400 mt-0.5 mb-4">Keys are encrypted on the server and never shown again, sent to visitors or written to logs. Only the new key is used to fetch highlights.</p>
      <div className="grid lg:grid-cols-2 gap-3 mb-4">
        <KeySlot slot="new" title="New key" hint="Used for all new highlights (from the start date onwards)." info={s?.keys?.new?.set ? s.keys.new : s?.envKeySet ? { set: true, updatedAt: null } : s?.keys?.new} onDone={load} />
        <KeySlot slot="old" title="Old key" hint="Kept on file only — never used for fetching." info={s?.keys?.old} onDone={load} />
      </div>
      {s && (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-lg bg-[#0A0C10]/60 border border-[#273042] px-3 py-2.5 mb-3" data-testid="sb-display">
            <span className="flex-1 min-w-[200px] text-sm text-white">Show ScoreBat videos to fans
              <span className="block text-[11px] text-slate-400">Embed token: {s.embedTokenSet ? "connected" : "not set"} · turn off if ScoreBat videos don't play — matches then show “Video soon”.</span></span>
            <button onClick={async () => { await endpoints.sbAdminDisplay(!s.hideClips); load(); }} data-testid="sb-display-toggle"
              className={`h-8 px-4 rounded-full text-xs font-bold ${s.hideClips ? "bg-slate-600 text-white" : "bg-emerald-500 text-black"}`}>{s.hideClips ? "Hidden" : "Showing"}</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Start date" value={fmt(`${s.startDate}T00:00:00Z`).split(",").slice(0, 2).join(",")} testid="sb-start-date" />
            <Stat label="Calls this month" value={s.callsThisMonth} testid="sb-calls-month" />
            <Stat label="Last call" value={fmt(s.lastCallAt)} testid="sb-last-call" />
            <Stat label="Highlights saved" value={`${s.stored} (${s.storedNew} new)`} testid="sb-stored" />
          </div>
          <p className="text-xs text-slate-400 mt-3 flex items-start gap-1.5" data-testid="sb-schedule-note">
            <CalendarClock className="w-4 h-4 shrink-0 text-slate-500" />
            {s.waitingForStart ? `Waiting for ${s.startDate} — no ScoreBat calls until then.`
              : `Checks at most once an hour, only after today's ${s.competitions.join(", ")} games have finished.`}
          </p>
          {s.pending?.length > 0 && <p className="text-xs text-amber-400 mt-1.5 flex items-start gap-1.5" data-testid="sb-pending"><Activity className="w-4 h-4 shrink-0" />Waiting for highlights: {s.pending.join(" · ")}</p>}
          {s.lastError && <p className="text-xs text-[#EF4444] mt-1.5" data-testid="sb-last-error">{s.lastError}</p>}
        </>
      )}
    </div>
  );
}
